import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChild, effect, ElementRef, inject, input, output, TemplateRef } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { MbsSize } from '../../../utils';
import { InputButton } from './input-button';

export type Position = 'prepend' | 'append';

@Component({
  selector: 'mbs-input-pre-append',
  templateUrl: './input-pre-append.component.html',
  styleUrls: ['./input-pre-append.component.scss'],
  imports: [NgbTooltip, NgClass, NgTemplateOutlet],
  host: {
    '(document:keyup.enter)': 'handleKeyupEnter($event)'
  }
})
export class InputPreAppendComponent {
  private readonly el = inject(ElementRef);

  public readonly contentTemplate = contentChild<TemplateRef<any> | null>(TemplateRef);

  public readonly buttons = input<InputButton[]>([], { alias: 'data' });
  public readonly buttonTemplate = input<TemplateRef<any> | null>(null, { alias: 'dataTemplate' });
  public readonly customClasses = input<string>('');
  public readonly disabled = input<boolean>(false);
  public readonly size = input<MbsSize.lg | MbsSize.sm>(null);
  public readonly position = input<'prepend' | 'append'>(null);

  protected readonly template = computed<TemplateRef<any> | null>(() => this.buttonTemplate() ?? this.contentTemplate());

  protected readonly buttonClick = output<InputButton>();
  protected readonly keyupEnter = output<InputButton>();

  protected readonly hostClasses = computed(() => {
    const pos = this.position();

    return pos ? `input-group-${pos}` : '';
  });

  protected readonly buttonsData = computed(() => {
    const buttons = this.buttons() || [];
    const customClasses = this.customClasses() || '';
    const defaultSize = this.size();

    return buttons.map((button) => {
      const size = button.size || defaultSize;

      const classes = [
        'btn',
        size ? `btn-${size}` : '',
        button.loading ? '-loading' : '',
        button.type ? `btn-${button.type}` : '',
        customClasses
      ].filter(Boolean);

      return { ...button, classes };
    });
  });

  protected readonly hostClassesEffect = effect(() => {
    const hostClasses = this.hostClasses();
    const el = this.el.nativeElement;

    el.classList.remove('input-group-prepend', 'input-group-append');

    if (hostClasses) {
      el.classList.add(hostClasses);
    }
  });

  handleKeyupEnter(event: InputButton): void {
    if (!event) return;

    this.keyupEnter.emit(event);
  }
}
